import { useState } from 'react'
import type { components } from '../../../api/generated/schema'
import {
  Button,
  CollapsibleCard,
  IconButton,
  Input,
  SectionLabel,
  Textarea,
} from '../../../design/components'
import type { CharacterEditing } from '../useCharacterEditing'
import { SHEET_SECTION_ID } from './sheetSections'
import styles from './sections.module.css'

type EquipmentSection = components['schemas']['EquipmentSectionDto']

export function EquipmentCard({
  equipment,
  editing,
}: {
  equipment: EquipmentSection | undefined
  editing?: CharacterEditing | undefined
}) {
  const [adding, setAdding] = useState(false)

  const items = equipment?.items ?? []
  const canEdit = editing?.canEdit === true

  if (equipment === undefined) return null
  if (items.length === 0 && !canEdit) return null

  const equipped = items.filter((item) => item.isEquipped === true).length

  return (
    <CollapsibleCard
      id={SHEET_SECTION_ID.equipment}
      empty={items.length === 0}
      variant="equipment"
      label="Equipment"
      summary={`${equipped}/${items.length} equipped`}
      action={
        canEdit && editing !== undefined && !adding ? (
          <IconButton label="Add equipment" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {items.length === 0 && <p className={styles.empty}>Nothing carried.</p>}

      {items.map((item) => (
        <div key={item.id} className={styles.row}>
          <div className={styles.rowHead}>
            <span className={styles.rowName}>{item.name}</span>
            {(item.power ?? 0) !== 0 && <span className={styles.rowBadge}>{item.power}</span>}
          </div>
          {item.description !== undefined && item.description !== '' && (
            <p className={styles.rowText}>{item.description}</p>
          )}
          <div className={styles.rowTags}>
            <span className={styles.tag}>{String(item.equipmentType ?? '')}</span>
            {item.equipmentSubtype !== undefined && item.equipmentSubtype !== 'None' && (
              <span className={styles.tag}>{String(item.equipmentSubtype)}</span>
            )}
            {item.isEquipped === true && <span className={styles.tag}>Equipped</span>}
          </div>

          {canEdit && editing !== undefined && (
            <div className={styles.rowActions}>
              <Button
                variant="secondary"
                disabled={editing.setEquipped.isPending}
                onClick={() =>
                  editing.setEquipped.mutate({
                    itemId: item.id ?? 0,
                    isEquipped: item.isEquipped !== true,
                  })
                }
              >
                {item.isEquipped === true ? 'Unequip' : 'Equip'}
              </Button>
              <Button
                variant="danger"
                disabled={editing.removeEquipment.isPending}
                onClick={() => editing.removeEquipment.mutate({ itemId: item.id ?? 0 })}
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      ))}

      {canEdit && editing !== undefined && adding && (
        <EquipmentForm editing={editing} onClose={() => setAdding(false)} />
      )}
    </CollapsibleCard>
  )
}

function EquipmentForm({ editing, onClose }: { editing: CharacterEditing; onClose: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [power, setPower] = useState('1')
  const [type, setType] = useState<'Weapon' | 'Armor'>('Weapon')
  const [subtype, setSubtype] = useState<'None' | 'Melee' | 'Ballistic' | 'Combined'>('Melee')

  const pending = editing.addEquipment.isPending

  return (
    <form
      className={styles.inlineForm}
      style={{ marginTop: 'var(--sp-4)' }}
      onSubmit={(event) => {
        event.preventDefault()
        editing.addEquipment.mutate(
          {
            name,
            description: description === '' ? null : description,
            power: Number(power) || 0,
            equipmentType: type,
            equipmentSubtype: subtype,
          },
          { onSuccess: onClose },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        New item
      </SectionLabel>

      <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <Input
        label="Power"
        type="number"
        inputMode="numeric"
        value={power}
        onChange={(e) => setPower(e.target.value)}
      />

      <label>
        <SectionLabel tone="muted" micro>
          Type
        </SectionLabel>
        <select
          className={styles.levelSelect}
          value={type}
          onChange={(e) => setType(e.target.value as 'Weapon' | 'Armor')}
        >
          <option value="Weapon">Weapon</option>
          <option value="Armor">Armor</option>
        </select>
      </label>

      <label>
        <SectionLabel tone="muted" micro>
          Subtype
        </SectionLabel>
        <select
          className={styles.levelSelect}
          value={subtype}
          onChange={(e) => setSubtype(e.target.value as typeof subtype)}
        >
          <option value="None">None</option>
          <option value="Melee">Melee</option>
          <option value="Ballistic">Ballistic</option>
          <option value="Combined">Combined</option>
        </select>
      </label>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || name.trim() === ''}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}
